import { useState, useEffect, useRef } from "react";
import "./scss/TimerElement.scss";

function TimerElement({ data, freeze, onTimeout }) {
	const time = data.countdown;
	const barHeight = data.barHeight ?? '5px';
	const showTime = data.showTime !== false;
	const label = data.label ?? null;
	const timeFontsize = data.timeFontsize ?? "1rem";
	const timeBackground = data.timeBackground? `url(${data.timeBackground})` : null;
	const timePadding = data.timePadding;

	const initialDigitCount = String(Math.ceil(data.countdown/1000)).length;
	const hasTimeBackground = timeBackground != null;

	// how much time has already elapsed when the timer mounts (ms), clamped to [0, time]
	const elapsed = Math.min(Math.max(0, data.elapsed ?? 0), time);
	const initialRemaining = time - elapsed;
	const initialWidth = `${(initialRemaining / time) * 100}%`;

	const [secondsLeft, setSecondsLeft] = useState(Math.ceil(initialRemaining / 1000));
	const barRef = useRef(null);
	// refs so toggling visibility / changing the callback doesn't reset the timer
	const onTimeoutRef = useRef(onTimeout);
	onTimeoutRef.current = onTimeout;
	const showTimeRef = useRef(showTime);
	showTimeRef.current = showTime;

	useEffect(() => {
		if (freeze) {
			if (barRef.current) barRef.current.style.width = initialWidth;
			return;
		}

		// pretend the timer started `elapsed` ms in the past
		const start = performance.now() - elapsed;
		let rafId;
		let finished = false;

		const tick = (now) => {
			const remaining = Math.max(0, time - (now - start));

			if (barRef.current) {
				barRef.current.style.width = `${(remaining / time) * 100}%`;
			}
			// only touch state when the counter is actually visible
			if (showTimeRef.current) {
				setSecondsLeft(Math.ceil(remaining / 1000));
			}

			if (remaining <= 0) {
				finished = true;
				onTimeoutRef.current?.();
				return;
			}
			rafId = requestAnimationFrame(tick);
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			if (!finished) cancelAnimationFrame(rafId);
		};
	}, [time, freeze, elapsed]);

	return (
		<div style={{ marginTop: data?.marginTop }} className="timer">
			{showTime && (
				<div
					className={`icon-container ${hasTimeBackground ? "fixed-width" : ""}`}
					style={{
						backgroundImage: timeBackground,
						padding: timePadding,
						"--timer-digits": initialDigitCount,
					}}
				>
					<div className="time-value" style={{ fontSize: timeFontsize }}>
						{secondsLeft}
					</div>
				</div>
			)}
			<div className="countdown-container">
				<div className="label">{label}</div>
				<div className="countdown">
					<div className="bar" ref={barRef} style={{ height: barHeight, width: initialWidth }}></div>
				</div>
			</div>
		</div>
	);
}

export default TimerElement;
